import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { FileText, CheckCircle2, PenTool, AlertTriangle, ShieldAlert, QrCode, Hammer, X } from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import { fmtDateTime } from "../lib/datetime";

const STATUSES = ["Requested", "RAMS Approved", "DRA Signed", "Erected", "Standing", "Struck"];

export default function StructureDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [countersignOpen, setCountersignOpen] = useState(null); // handover id or null
  const [handoverPromptOpen, setHandoverPromptOpen] = useState(false);
  const canEdit = ["admin", "contracts_manager", "supervisor", "competent_person"].includes(user?.role);

  const load = () => api.get(`/structures/${id}`).then((r) => setS(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (status) => {
    const prev = s?.status;
    try {
      await api.patch(`/structures/${id}/status`, { status });
      // iter-29 chain: on entering Erected, prompt the supervisor to start the handover.
      if (status === "Erected" && prev !== "Erected") {
        setHandoverPromptOpen(true);
      }
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update status");
    }
  };

  if (!s) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div data-testid="structure-detail" style={{ padding: "24px 24px 64px" }}>
      <Link to="/structures" className="mono" style={{ color: "#64748b", fontSize: 12 }}>← Back to register</Link>
      <h1 style={{ fontSize: 28, fontWeight: 900, margin: "8px 0 0" }}>{s.description}</h1>
      <div className="mono" style={{ color: "#64748b", fontSize: 12 }}>{s.id} · {s.area_name} · {s.zone_classification}</div>

      {s.tagged_out ? (
        <div className="card" data-testid="tagged-out-banner" style={{ padding: 14, marginTop: 14, borderLeft: "6px solid #dc2626", background: "#fef2f2" }}>
          <div style={{ fontWeight: 800, color: "#b91c1c", display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={18} /> SCAFFOLD TAGGED OUT — DO NOT USE
          </div>
          <div className="mono" style={{ color: "#7f1d1d", marginTop: 4, fontSize: 13 }}>{s.tagged_out_reason || "Safety-critical defect logged at inspection."}</div>
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 20 }}>
        <Field label="Status" value={s.status} />
        <Field label="Rate ref" value={s.price_schedule_ref} />
        <Field label="Quantity" value={`${s.quantity} ${s.unit}`} />
        <Field label="Erected" value={s.erected_date ? fmtDateTime(s.erected_date) : "—"} />
        <Field label="Struck" value={s.struck_date ? fmtDateTime(s.struck_date) : "—"} />
        <Field label="GPS" value={s.gps_lat ? `${s.gps_lat.toFixed(5)}, ${s.gps_lng?.toFixed(5)}` : "—"} mono />
        <Field label="Source" value={s.source} />
      </div>

      {canEdit ? (
        <div className="card" style={{ padding: 16, marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569", marginBottom: 8 }}>Change status</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUSES.map((st) => (
              <button key={st} data-testid={`status-btn-${st.replace(/\s+/g, "-")}`} className={st === s.status ? "btn-primary" : "btn-ghost"} onClick={() => updateStatus(st)} disabled={st === s.status}>{st}</button>
            ))}
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed #e2e8f0", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <Link
              data-testid="structure-open-rams"
              to={`/rams?structure_id=${s.id}`}
              className="btn-primary"
              style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <ShieldAlert size={14} /> RAMS for this structure
            </Link>
            <a
              data-testid="structure-scaff-tag-pdf"
              href={`${API}/structures/${s.id}/scaff-tag/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost"
              style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, borderColor: "#f59e0b", color: "#7c2d12" }}
              title="Prints an A6-landscape scaff tag PDF (2 per A4 sheet) with a QR code linking to this structure's live history"
            >
              <QrCode size={14} /> Print scaff tag
            </a>
            <span className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>
              Each structure has its own RAMS · Scaff tag prints A6 landscape (2 per sheet) with a QR to the live audit chain.
            </span>
          </div>
        </div>
      ) : null}

      <div className="card" style={{ padding: 0, marginTop: 20 }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", fontWeight: 700 }}>Inspection history</div>
        <table>
          <thead>
            <tr><th>Report</th><th>Date</th><th>Result</th><th>SC / PA</th><th>Client</th><th>PDF</th></tr>
          </thead>
          <tbody>
            {(s.inspections || []).map((i) => (
              <tr key={i.id} data-testid={`insp-row-${i.id}`}>
                <td className="mono" style={{ fontWeight: 700 }}>{i.report_no || i.id.slice(0, 8)}</td>
                <td className="mono">{fmtDateTime(i.inspection_date)}</td>
                <td>{i.overall_result === "Pass" || i.result === "Pass" ? <span className="badge badge-green">Pass</span> : <span className="badge badge-red">Tagged out</span>}</td>
                <td className="mono">{i.sc_count != null ? `SC ${i.sc_count} · PA ${i.pa_count}` : "—"}</td>
                <td>{i.client_receipt ? <span className="badge badge-green">{i.client_receipt.name}</span> : <span className="badge badge-slate">—</span>}</td>
                <td>{i.report_no ? <a href={`${API}/inspections/${i.id}/pdf`} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ padding: "4px 10px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`insp-pdf-${i.id}`}><FileText size={12} /> PDF</a> : "—"}</td>
              </tr>
            ))}
            {!(s.inspections || []).length ? <tr><td colSpan={6} style={{ textAlign: "center", padding: 16, color: "#94a3b8" }}>No inspections logged</td></tr> : null}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: 0, marginTop: 20 }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", fontWeight: 700 }}>Handover certificates</div>
        <table>
          <thead>
            <tr>
              <th>Cert ref</th>
              <th>Issued</th>
              <th>Type · duty</th>
              <th>TG20:21</th>
              <th>McCain receipt</th>
              <th>PDF</th>
            </tr>
          </thead>
          <tbody>
            {(s.handovers || []).map((h) => (
              <tr key={h.id} data-testid={`handover-row-${h.id}`}>
                <td className="mono" style={{ fontWeight: 700 }}>{h.cert_ref || h.id.slice(0, 8)}</td>
                <td className="mono">{fmtDateTime(h.issued_at)}</td>
                <td>{h.scaffold_type || "—"}<div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{h.duty_class || ""}</div></td>
                <td>{h.tg20_compliant ? <span className="badge badge-green">TG20:21</span> : <span className="badge badge-amber">Bespoke {h.bespoke_design_ref}</span>}</td>
                <td>
                  {h.mccain_receipt ? (
                    <span className="badge badge-green"><CheckCircle2 size={11} /> {h.mccain_receipt.name}</span>
                  ) : (
                    <button data-testid={`countersign-btn-${h.id}`} className="btn-ghost" onClick={() => setCountersignOpen(h.id)} style={{ padding: "4px 10px", fontSize: 12 }}>
                      <PenTool size={12} style={{ verticalAlign: -2, marginRight: 4 }} /> Countersign
                    </button>
                  )}
                </td>
                <td>
                  <a data-testid={`pdf-link-${h.id}`} href={`${API}/handovers/${h.id}/pdf`} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ padding: "4px 10px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <FileText size={12} /> PDF
                  </a>
                </td>
              </tr>
            ))}
            {!(s.handovers || []).length ? <tr><td colSpan={6} style={{ textAlign: "center", padding: 16, color: "#94a3b8" }}>No handovers yet</td></tr> : null}
          </tbody>
        </table>
      </div>

      {countersignOpen ? <CountersignModal handoverId={countersignOpen} onClose={() => setCountersignOpen(null)} onDone={() => { setCountersignOpen(null); load(); }} /> : null}
      {handoverPromptOpen ? (
        <div data-testid="handover-prompt-modal" onClick={() => setHandoverPromptOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "white", maxWidth: 460, width: "100%", padding: 22, position: "relative", borderTop: "4px solid #059669" }}>
            <button data-testid="handover-prompt-close" onClick={() => setHandoverPromptOpen(false)} className="btn-ghost" style={{ position: "absolute", top: 10, right: 10 }}><X size={14} /></button>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <Hammer size={20} color="#059669" />
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Structure marked Erected</h2>
            </div>
            <p style={{ fontSize: 13, color: "#475569", margin: "6px 0 14px" }}>
              Next step in the client-agreed chain: issue the handover certificate to McCain. This locks in the scaffold spec, captures your competent-person signature, and moves the structure to <strong>Standing</strong> once complete.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button data-testid="handover-prompt-later" onClick={() => setHandoverPromptOpen(false)} className="btn-ghost">Do it later</button>
              <button
                data-testid="handover-prompt-start"
                onClick={() => navigate(`/handover?structure_id=${id}`)}
                className="btn-primary"
                style={{ background: "#059669", borderColor: "#059669" }}
              >
                Start handover <CheckCircle2 size={13} style={{ marginLeft: 4, verticalAlign: -2 }} />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CountersignModal({ handoverId, onClose, onDone }) {
  const [name, setName] = useState("");
  const [position, setPosition] = useState("Site engineer");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/handovers/${handoverId}/countersign`, { mccain_name: name, mccain_position: position, mccain_signature: signature });
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="countersign-modal" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 100 }} onClick={onClose}>
      <div className="card" style={{ width: "100%", maxWidth: 520, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>McCain receipt · countersign</h2>
        <p style={{ color: "#64748b", marginTop: 0 }}>The McCain contact countersigns the certificate on this device to confirm receipt.</p>
        <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Print name</label>
            <input data-testid="cs-name" className="input-base" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Position</label>
            <input data-testid="cs-position" className="input-base" value={position} onChange={(e) => setPosition(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>Signature</label>
            <SignaturePad value={signature} onChange={setSignature} testid="cs-signature" />
          </div>
          {err ? <div className="badge badge-red" style={{ padding: "8px 12px" }}>{String(err)}</div> : null}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button data-testid="cs-submit" type="submit" className="btn-primary" disabled={busy || !signature || !name.trim()}>{busy ? "Saving…" : "Countersign"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, value, mono }) => (
  <div className="card" style={{ padding: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#475569" }}>{label}</div>
    <div className={mono ? "mono" : ""} style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{value}</div>
  </div>
);
